import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth0 } from '@auth0/auth0-react'
import { useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { PolicyId } from '@estercare/ester-shared'
import { QueryKeys } from '../../api/ester-b2b-api/types'
import { useGetPolicyDocument } from '../../api/ester-b2b-api/public/policies/policiesQueries'
import { PolicyVersionOutdatedError } from '../../api/ester-b2b-api/private/policies/policies'
import {
  useAcknowledgePolicyMutation,
  useDeclinePolicyMutation,
} from '../../api/ester-b2b-api/private/policies/policiesQueries'
import { OnClickButton } from '../../components/buttons/onclick/OnClickButton'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import { BodyLarge, BodyMedium, BodySmall, Title2, Title3, Title6 } from '../../components/styles/Typography'
import { useSelectUserEmail } from '../../state/selectors'
import { useUserStore } from '../../state/store'
import { theme } from '../../theme/theme'
import { queryErrorHandler } from '../../utils/queryErrorHandler'
import { DeclinePolicyDialog } from './DeclinePolicyDialog'
import { dataPolicyPath } from './dataPolicyPath'
import { PolicyDocumentBody } from './PolicyDocumentBody'
import { PolicyCard, PolicyCardBody, PolicyCardTop, PolicyPageMain, PolicyPageShell } from './PolicyPageShell'

export const DataPolicyStep = () => {
  const { t, i18n } = useTranslation()
  const { logout } = useAuth0()
  const queryClient = useQueryClient()
  const { setUser } = useUserStore()
  const email = useSelectUserEmail()
  const acknowledgedVersion = useUserStore((state) => state.user?.dataPolicy?.acknowledgedVersion)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showDeclineDialog, setShowDeclineDialog] = useState(false)
  const [isDeclining, setIsDeclining] = useState(false)
  const [versionChanged, setVersionChanged] = useState(false)
  const [hasSubmitError, setHasSubmitError] = useState(false)
  const [showFullPolicy, setShowFullPolicy] = useState(false)

  const {
    data: policy,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useGetPolicyDocument({ policyId: PolicyId.DATA_POLICY, language: i18n.language })
  const { mutate: acknowledge, isPending: isAcknowledging } = useAcknowledgePolicyMutation({
    onSuccess: (_response, { version }) => {
      const currentUser = useUserStore.getState().user
      if (currentUser) {
        setUser({
          ...currentUser,
          dataPolicy: { currentVersion: version, acknowledgedVersion: version, acknowledgementRequired: false },
        })
      }
    },
    onError: (error) => {
      if (error instanceof PolicyVersionOutdatedError) {
        setVersionChanged(true)
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.Policy] })
      } else {
        setHasSubmitError(true)
      }
    },
  })
  const { mutateAsync: declineAsync } = useDeclinePolicyMutation()

  const isUpdated = Boolean(acknowledgedVersion) || versionChanged
  const changeSummary = isUpdated ? policy?.changeSummary : undefined

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [policy?.version, showFullPolicy])

  const handleAcknowledge = () => {
    if (!policy) return
    setHasSubmitError(false)
    acknowledge({ policyId: PolicyId.DATA_POLICY, version: policy.version, language: policy.language })
  }

  const handleDecline = async () => {
    setIsDeclining(true)
    await declineAsync(PolicyId.DATA_POLICY).catch(queryErrorHandler('record policy decline'))
    logout({ logoutParams: { returnTo: `${window.location.origin}/policy-declined` } })
  }

  const headerRight = email && (
    <LoggedInAs>
      {t('common.dataPolicy.loggedInAs')} <strong>{email}</strong>
    </LoggedInAs>
  )

  const whatsNew = changeSummary && changeSummary.length > 0 && policy && (
    <WhatsNew>
      <Title6>{t('common.dataPolicy.whatsNew', { version: policy.version })}</Title6>
      <ul>
        {changeSummary.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </WhatsNew>
  )

  const loadError = isError && (
    <ErrorBox>
      <BodyLarge>{t('common.dataPolicy.loadError')}</BodyLarge>
      <OnClickButton text={t('common.dataPolicy.retry')} onClick={() => void refetch()} />
    </ErrorBox>
  )

  const submitError = hasSubmitError && <SubmitError role="alert">{t('common.dataPolicy.submitError')}</SubmitError>

  const buttons = (
    <Buttons>
      <OnClickButton
        secondary
        text={t('common.dataPolicy.decline')}
        onClick={() => setShowDeclineDialog(true)}
        disabled={isAcknowledging}
      />
      <OnClickButton
        text={t('common.dataPolicy.acknowledge')}
        onClick={handleAcknowledge}
        disabled={!policy || isAcknowledging || isFetching}
        loading={isAcknowledging}
      />
    </Buttons>
  )

  const declineDialog = showDeclineDialog && (
    <DeclinePolicyDialog
      onConfirm={() => void handleDecline()}
      onCancel={() => setShowDeclineDialog(false)}
      loading={isDeclining}
    />
  )

  if (!showFullPolicy) {
    return (
      <PolicyPageShell headerRight={headerRight}>
        <CompactMain>
          <CompactCard>
            <Title3>{isUpdated ? t('common.dataPolicy.titleUpdated') : t('common.dataPolicy.title')}</Title3>
            <CompactText>
              {isUpdated ? t('common.dataPolicy.summaryUpdated') : t('common.dataPolicy.summary')}
            </CompactText>
            {whatsNew}
            {loadError}
            <ReadPolicyButton type="button" onClick={() => setShowFullPolicy(true)}>
              {t('common.dataPolicy.readPolicy')}
            </ReadPolicyButton>
            <CompactHint>{t('common.dataPolicy.compactHint')}</CompactHint>
            {submitError}
            {buttons}
          </CompactCard>
        </CompactMain>
        {declineDialog}
      </PolicyPageShell>
    )
  }

  return (
    <PolicyPageShell headerRight={headerRight}>
      <PolicyPageMain>
        <Title>{isUpdated ? t('common.dataPolicy.titleUpdated') : t('common.dataPolicy.title')}</Title>
        <Ingress>{isUpdated ? t('common.dataPolicy.ingressUpdated') : t('common.dataPolicy.ingress')}</Ingress>
        {whatsNew}
        <PolicyCard>
          <PolicyCardTop>
            <span>
              {policy && t('common.dataPolicy.versionLine', { version: policy.version, date: policy.effectiveFrom })}
            </span>
            <a href={dataPolicyPath(i18n.language)} target="_blank" rel="noopener noreferrer">
              {t('common.dataPolicy.openInNewWindow')} ↗
            </a>
          </PolicyCardTop>
          <Fade>
            <PolicyScroll ref={scrollRef}>
              {isLoading && <LoadingSpinner size={40} color={theme.color.plum} />}
              {loadError}
              {policy && <PolicyDocumentBody markdown={policy.markdown} />}
            </PolicyScroll>
          </Fade>
        </PolicyCard>
      </PolicyPageMain>
      <Actions>
        {submitError}
        <ActionsInner>
          <Hint>{t('common.dataPolicy.hint')}</Hint>
          {buttons}
        </ActionsInner>
      </Actions>
      {declineDialog}
    </PolicyPageShell>
  )
}

const CompactMain = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.mediumLarge}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    align-items: flex-start;
    padding: ${theme.spacing.xsmall}px ${theme.spacing.medium}px ${theme.spacing.mediumLarge}px;
  }
`

const CompactCard = styled.div`
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  padding: ${theme.spacing.large}px;
  background: ${theme.color.white};
  border: 1px solid #0000000d;
  border-radius: ${theme.spacing.medium}px;

  h3 {
    color: ${theme.color.plum};
  }

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    padding: ${theme.spacing.mediumLarge}px;
  }
`

const CompactText = styled(BodyLarge)`
  color: ${theme.color.grey5};
`

const ReadPolicyButton = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  font-size: ${theme.fontSize.default}px;
  color: ${theme.color.plumMid};
  text-decoration: underline;
`

const CompactHint = styled(BodyMedium)`
  color: ${theme.color.grey5};
`

const LoggedInAs = styled(BodySmall)`
  color: ${theme.color.grey5};
  text-align: right;
  overflow-wrap: anywhere;

  strong {
    font-weight: 500;
    color: ${theme.color.black};
  }
`

const Title = styled(Title2)`
  margin: ${theme.spacing.xsmall}px 0 ${theme.spacing.small}px;
  color: ${theme.color.plum};
`

const Ingress = styled(BodyLarge)`
  margin-bottom: ${theme.spacing.mediumLarge}px;
  font-size: ${theme.fontSize.medium}px;
  line-height: 28px;
  color: ${theme.color.grey5};
`

const WhatsNew = styled.div`
  margin-bottom: 20px;
  padding: ${theme.spacing.medium}px 20px;
  background: ${theme.color.plumLight};
  border: 1px solid ${theme.color.plumMidPale};
  border-radius: ${theme.spacing.small}px;

  h6 {
    margin-bottom: 6px;
  }

  ul {
    margin: 0;
    padding-left: 20px;
    list-style-type: disc;
  }
`

const Fade = styled.div`
  position: relative;

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 40px;
      background: linear-gradient(#fff0, #fff);
      pointer-events: none;
    }
  }
`

const PolicyScroll = styled(PolicyCardBody)`
  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    max-height: 52vh;
    overflow-y: auto;
  }
`

const ErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacing.medium}px;
  padding-top: ${theme.spacing.mediumLarge}px;
`

const Actions = styled.div`
  position: sticky;
  bottom: 0;
  background: ${theme.color.beigeLight};
  border-top: 1px solid #0000000d;
  padding: ${theme.spacing.medium}px ${theme.spacing.mediumLarge}px;
`

const ActionsInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.small}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const SubmitError = styled(BodySmall)`
  max-width: 760px;
  margin: 0 auto ${theme.spacing.xsmall}px;
  color: ${theme.color.red};
`

const Hint = styled(BodySmall)`
  flex: 1;
  min-width: 220px;
  color: ${theme.color.grey5};

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    display: none;
  }
`

const Buttons = styled.div`
  display: flex;
  gap: ${theme.spacing.small}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`
